/**
 * ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
 * authStore.ts — 회원가입 / 로그인 / 세션 관리
 *
 * 【구조】 API-first (MongoDB 백엔드) → localStorage 세션 캐시
 *   · 회원가입/로그인 → POST /api/v1/auth/register, /login
 *   · JWT 토큰 localStorage 보관 (SESSION_KEY)
 *   · 백엔드 미연결 시 localStorage fallback 자동 전환
 * ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
 */

import { create } from 'zustand'

// ── 환경에 따른 API base URL ──────────────────────────────────────────────────
const API_BASE = import.meta.env.VITE_API_URL ?? 'http://localhost:8000/api/v1'

// ── 로컬스토리지 키 ────────────────────────────────────────────────────────────
const SESSION_KEY = 'math_tutor_session_v2'   // { token, user }
const FALLBACK_KEY = 'math_tutor_accounts_v1' // fallback only

// ── 타입 ──────────────────────────────────────────────────────────────────────

export interface AuthUser {
  id: string
  name: string
  email: string
  grade: 1 | 2 | 3
  role: 'student' | 'admin'   // 권한 레벨
  lastUnitId: string
  lastStepId: string
  createdAt: string
  lastLoginAt: string
}

export interface AuthSession {
  token: string
  user: AuthUser
}

// ── localStorage 세션 캐시 헬퍼 ───────────────────────────────────────────────

const sessionCache = {
  load(): AuthSession | null {
    try { return JSON.parse(localStorage.getItem(SESSION_KEY) ?? 'null') } catch { return null }
  },
  save(s: AuthSession | null) {
    if (s) localStorage.setItem(SESSION_KEY, JSON.stringify(s))
    else localStorage.removeItem(SESSION_KEY)
  },
}

// ── API 헬퍼 ──────────────────────────────────────────────────────────────────

async function apiFetch(path: string, options: RequestInit = {}): Promise<Response> {
  return fetch(`${API_BASE}${path}`, {
    headers: { 'Content-Type': 'application/json', ...(options.headers ?? {}) },
    ...options,
  })
}

// ── fallback: localStorage 전용 (백엔드 없을 때) ──────────────────────────────

function simpleHash(str: string): string {
  let h = 0x811c9dc5
  for (let i = 0; i < str.length; i++) { h ^= str.charCodeAt(i); h = (h * 0x01000193) >>> 0 }
  return h.toString(16).padStart(8, '0')
}

interface FallbackAccount {
  id: string; name: string; email: string; passwordHash: string
  grade: 1|2|3; createdAt: string; lastLoginAt: string
  lastUnitId: string; lastStepId: string
}

const fallbackDB = {
  loadAll(): Record<string, FallbackAccount> {
    try { return JSON.parse(localStorage.getItem(FALLBACK_KEY) ?? '{}') } catch { return {} }
  },
  save(accounts: Record<string, FallbackAccount>) {
    localStorage.setItem(FALLBACK_KEY, JSON.stringify(accounts))
  },
}

function fallbackToAuthUser(a: FallbackAccount): AuthUser {
  return { id: a.id, name: a.name, email: a.email, grade: a.grade,
           role: 'student',
           lastUnitId: a.lastUnitId, lastStepId: a.lastStepId,
           createdAt: a.createdAt, lastLoginAt: a.lastLoginAt }
}

// ── Zustand store ──────────────────────────────────────────────────────────────

interface AuthState {
  session: AuthSession | null
  isLoggedIn: boolean
  isAdmin: boolean       // session.user.role === 'admin'
  useFallback: boolean   // true = 백엔드 미연결, localStorage 모드

  initAuth: () => Promise<void>
  register: (p: { name: string; email: string; password: string; grade: 1|2|3 }) => Promise<{ ok: boolean; error?: string }>
  login: (email: string, password: string) => Promise<{ ok: boolean; error?: string }>
  logout: () => void
  updateLastStudied: (unitId: string, stepId: string) => Promise<void>
  getToken: () => string | null
}

export const useAuthStore = create<AuthState>((set, get) => ({
  session: null,
  isLoggedIn: false,
  isAdmin: false,
  useFallback: false,

  // ── 앱 시작 시 기존 세션 복원 ─────────────────────────────────────────────
  async initAuth() {
    const cached = sessionCache.load()
    if (!cached) return

    // 백엔드로 토큰 검증
    try {
      const res = await apiFetch('/auth/me', {
        headers: { Authorization: `Bearer ${cached.token}` },
      })
      if (res.ok) {
        const user: AuthUser = await res.json()
        const session = { token: cached.token, user }
        sessionCache.save(session)
        set({ session, isLoggedIn: true, isAdmin: user.role === 'admin', useFallback: false })
        return
      }
    } catch {
      // 백엔드 미연결 → fallback 모드
    }

    // fallback: 캐시된 세션 그대로 사용 (백엔드 미연결)
    set({ session: cached, isLoggedIn: true, isAdmin: cached.user.role === 'admin', useFallback: true })
  },

  // ── 회원가입 ──────────────────────────────────────────────────────────────
  async register({ name, email, password, grade }) {
    const emailLower = email.toLowerCase().trim()

    // --- API 시도 ---
    try {
      const res = await apiFetch('/auth/register', {
        method: 'POST',
        body: JSON.stringify({ name: name.trim(), email: emailLower, password, grade }),
      })
      if (res.ok) {
        const data: AuthSession = await res.json()
        sessionCache.save(data)
        set({ session: data, isLoggedIn: true, isAdmin: data.user.role === 'admin', useFallback: false })
        return { ok: true }
      }
      const err = await res.json().catch(() => ({}))
      return { ok: false, error: (err as any).detail ?? '회원가입에 실패했습니다.' }
    } catch {
      // 백엔드 없음 → fallback
    }

    // --- localStorage fallback ---
    if (!name.trim()) return { ok: false, error: '이름을 입력해주세요.' }
    if (!emailLower.includes('@')) return { ok: false, error: '올바른 이메일을 입력해주세요.' }
    if (password.length < 4) return { ok: false, error: '비밀번호는 4자 이상이어야 합니다.' }
    const accounts = fallbackDB.loadAll()
    if (Object.values(accounts).find(a => a.email === emailLower))
      return { ok: false, error: '이미 가입된 이메일입니다.' }
    const now = new Date().toISOString()
    const id = `local_${Date.now()}`
    const account: FallbackAccount = {
      id, name: name.trim(), email: emailLower,
      passwordHash: simpleHash(password), grade,
      createdAt: now, lastLoginAt: now,
      lastUnitId: 'F2-1', lastStepId: 'LS01',
    }
    accounts[id] = account
    fallbackDB.save(accounts)
    const user = fallbackToAuthUser(account)
    const session: AuthSession = { token: `local_${id}`, user }
    sessionCache.save(session)
    set({ session, isLoggedIn: true, isAdmin: false, useFallback: true })
    return { ok: true }
  },

  // ── 로그인 ────────────────────────────────────────────────────────────────
  async login(email, password) {
    const emailLower = email.toLowerCase().trim()

    // --- API 시도 ---
    try {
      const res = await apiFetch('/auth/login', {
        method: 'POST',
        body: JSON.stringify({ email: emailLower, password }),
      })
      if (res.ok) {
        const data: AuthSession = await res.json()
        sessionCache.save(data)
        set({ session: data, isLoggedIn: true, isAdmin: data.user.role === 'admin', useFallback: false })
        return { ok: true }
      }
      const err = await res.json().catch(() => ({}))
      return { ok: false, error: (err as any).detail ?? '로그인에 실패했습니다.' }
    } catch {
      // 백엔드 없음 → fallback
    }

    // --- localStorage fallback ---
    const accounts = fallbackDB.loadAll()
    const account = Object.values(accounts).find(a => a.email === emailLower)
    if (!account) return { ok: false, error: '가입되지 않은 이메일입니다.' }
    if (account.passwordHash !== simpleHash(password)) return { ok: false, error: '비밀번호가 틀렸습니다.' }
    const now = new Date().toISOString()
    account.lastLoginAt = now
    accounts[account.id] = account
    fallbackDB.save(accounts)
    const user = fallbackToAuthUser(account)
    const session: AuthSession = { token: `local_${account.id}`, user }
    sessionCache.save(session)
    set({ session, isLoggedIn: true, isAdmin: false, useFallback: true })
    return { ok: true }
  },

  // ── 로그아웃 ──────────────────────────────────────────────────────────────
  logout() {
    sessionCache.save(null)
    set({ session: null, isLoggedIn: false, isAdmin: false })
  },

  // ── 마지막 학습 위치 업데이트 ──────────────────────────────────────────────
  async updateLastStudied(unitId: string, stepId: string) {
    const { session, useFallback } = get()
    if (!session) return

    if (!useFallback) {
      try {
        const res = await apiFetch('/auth/me', {
          method: 'PUT',
          headers: { Authorization: `Bearer ${session.token}` },
          body: JSON.stringify({ lastUnitId: unitId, lastStepId: stepId }),
        })
        if (res.ok) {
          const user: AuthUser = await res.json()
          const updated = { ...session, user }
          sessionCache.save(updated)
          set({ session: updated })
          return
        }
      } catch { /* fallback */ }
    }

    // fallback: localStorage 업데이트
    const accounts = fallbackDB.loadAll()
    const acc = accounts[session.user.id]
    if (acc) {
      acc.lastUnitId = unitId
      acc.lastStepId = stepId
      accounts[acc.id] = acc
      fallbackDB.save(accounts)
    }
    const updatedUser = { ...session.user, lastUnitId: unitId, lastStepId: stepId }
    const updated = { ...session, user: updatedUser }
    sessionCache.save(updated)
    set({ session: updated })
  },

  // ── JWT 토큰 반환 ──────────────────────────────────────────────────────────
  getToken() {
    return get().session?.token ?? null
  },
}))
